import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { BookOpen, User, Mail, ArrowRight, Clock, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleStudentLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter your email and password');
      return;
    }

    setLoading(true);
    try {
      // Login with email and password
      const response = await axios.post(`${API}/student/login`, {
        email: email,
        password: password
      });
      const student = response.data;
      
      if (student) {
        // Store student data in sessionStorage
        sessionStorage.setItem('student_data', JSON.stringify(student));
        
        if (student.enrollment_paid) {
          // Student has paid enrollment - take them to dashboard
          toast.success('Welcome back! Accessing your student dashboard.');
          navigate('/student-dashboard');
        } else {
          // Student exists but hasn't paid enrollment
          toast.info('Please complete your enrollment payment to access your dashboard.');
          navigate('/enroll');
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Email not found. Please check your email or start a new enrollment.');
      } else if (error.response?.status === 401) {
        toast.error('Incorrect password. Please try again.');
      } else if (error.response?.status === 400) {
        // No password set - direct to forgot password
        toast.error('Password not set. Please use "Forgot Password" to create one.');
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-25 to-indigo-50">
      {/* Header with new GIF */}
      <div className="w-full">
        <img 
          src="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/csgwpycc_train%20to%20become%20a%20video%20review%20specialist.gif"
          alt="Train to Become a Video Review Specialist"
          className="w-full h-48 md:h-64 lg:h-80 object-cover object-center"
        />
        <div className="bg-white py-6">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
              Student Portal
            </h1>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/inexx1ig_IMG-20250713-WA0004%281%29.jpg"
                alt="Book Review Specialists Logo"
                className="w-6 h-6 rounded-full object-cover mr-2"
              />
              Book Review Specialists
            </Link>
            <h2 className="text-2xl font-bold text-slate-800">Student Dashboard</h2>
            <p className="text-slate-600 mt-2">Enter your email to access your course dashboard</p>
          </div>

          {/* Login Form */}
          <Card className="card">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800">Dashboard Access</h3>
            </div>

            <form onSubmit={handleStudentLogin} className="space-y-6">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your registered email"
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
                <div className="text-right mt-2">
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    Forgot Password?
                  </Link>
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full btn btn-primary"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="spinner mr-2"></div>
                    Accessing dashboard...
                  </div>
                ) : (
                  <>
                    Access My Course Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-slate-600">
                New to Book Review Specialists?{' '}
                <Link to="/enroll" className="text-blue-600 hover:text-blue-700 font-medium">
                  Start Your Enrollment
                </Link>
              </p>
            </div>
          </Card>

          {/* Security and Terms Notice */}
          <Card className="mt-6 p-4 bg-slate-50 border-slate-200">
            <div className="flex items-start">
              <BookOpen className="w-5 h-5 text-slate-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-slate-800">Important Notice</h4>
                <p className="text-sm text-slate-700 mt-1">
                  Students must not share their login credentials with anyone. By accessing our portal, you are agreeing to our{' '}
                  <Link to="/terms-of-sale" className="text-blue-600 hover:text-blue-700 underline">
                    Terms of Use
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-700 underline">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default StudentLogin;